import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { Link } from 'react-router';
import { formatDate } from '@xdocs/i18n';
import { Button, Mascot, Spinner, cn, useI18n } from '@xdocs/ui';
import { useCreditPacks } from '../settings/billing/useCreditPacks.js';
import { useCheckoutReturn } from './useCheckoutReturn.js';
import { useCheckoutT } from './useCheckoutT.js';
import styles from './CheckoutSuccessPage.module.css';

/**
 * `/checkout/success` (F45). Paddle's hosted checkout redirects here (see
 * `settings/billing/useCheckoutStart.ts`'s doc comment) with `?kind=...` +
 * `creditPackId` (credit-pack purchases only) carried along by THIS app,
 * not Paddle — `billing.checkout.start`'s own `returnUrl` is built with
 * those params attached.
 */
export function CheckoutSuccessPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useCheckoutT();
  const [searchParams] = useSearchParams();
  const kind = searchParams.get('kind') === 'credit_pack' ? 'credit_pack' : 'pro_subscription';
  const creditPackId = searchParams.get('creditPackId');
  const result = useCheckoutReturn(kind);
  const creditPacks = useCreditPacks();

  const pack = creditPackId ? creditPacks.packs.find((p) => p.id === creditPackId) : undefined;

  if (result.phase === 'confirming' && result.timedOut) {
    return (
      <div className={cn(styles['page'])}>
        <Mascot pose="thinking" size={120} alt="" />
        <h1 className={cn(styles['title'])}>{t('checkout:success.stillConfirming.title')}</h1>
        <p className={cn(styles['description'])}>{t('checkout:success.stillConfirming.description')}</p>
        <Link to="/settings/billing">
          <Button variant="primary">{t('checkout:success.stillConfirming.action')}</Button>
        </Link>
      </div>
    );
  }

  if (result.phase === 'confirming') {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Spinner size={32} />
        <h1 className={cn(styles['title'])}>
          {kind === 'pro_subscription' ? t('checkout:success.confirming.title') : t('checkout:success.confirming.creditPack.title')}
        </h1>
        <p className={cn(styles['description'])}>{t('checkout:success.confirming.description')}</p>
        {/* Confirming can take up to MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS
         * (~16s for pro_subscription) before `timedOut` flips — that
         * crosses the >10s "always give an escape/cancel path" bar, and
         * unlike the `timedOut` branch above this one previously had no
         * action at all. */}
        <Link to="/settings/billing">
          <Button variant="ghost">{t('checkout:success.confirming.action')}</Button>
        </Link>
      </div>
    );
  }

  // confirmed
  if (kind === 'credit_pack') {
    return (
      <div className={cn(styles['page'])}>
        <Mascot pose="celebrating" size={120} alt="" />
        <h1 className={cn(styles['title'])}>
          {pack ? t('checkout:success.confirmed.creditPack.title', { packName: pack.slug, count: pack.creditsAmount }) : t('checkout:success.action.continue')}
        </h1>
        <Link to="/settings/billing">
          <Button variant="primary">{t('checkout:success.action.continue')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className={cn(styles['page'])}>
      <Mascot pose="celebrating" size={120} alt="" />
      <h1 className={cn(styles['title'])}>{t('checkout:success.confirmed.subscription.title')}</h1>
      <p className={cn(styles['description'])}>{t('checkout:success.confirmed.subscription.description')}</p>
      {result.subscription?.currentPeriodEnd && (
        <p className={cn(styles['renewsLine'])}>
          {t('checkout:success.confirmed.subscription.renews', { date: formatDate(locale, result.subscription.currentPeriodEnd, { dateStyle: 'medium' }) })}
        </p>
      )}
      <Link to="/">
        <Button variant="primary">{t('checkout:success.action.start')}</Button>
      </Link>
    </div>
  );
}
